/**
 * Vista completa de notificaciones
 * -----------------------------------------------------------------------------
 * A donde lleva el «Ver todas» de la campana. Mismo dato y mismo aspecto, pero
 * con sitio para leerlas con calma: filtro, agrupación por día y sin el límite
 * de alto del panel.
 *
 *   CompanyTheme.notificationList('#lista', {
 *     items: notificaciones,
 *     onRead: (item) => marcarLeida(item.id),
 *     onReadAll: () => marcarTodas(),
 *   });
 *
 * Comparte el pintado de cada notificación con la campana —`renderNotification`—
 * para que no haya dos maneras de dibujar lo mismo que acaben divergiendo.
 *
 * Agrupa por día si las notificaciones traen `date`. Sin ese campo van todas
 * seguidas: el theme no inventa fechas a partir del texto de `time`, que es
 * libre y puede decir «ayer» o «hace un rato».
 */

import { notificationBellES, dataViewES } from './locale-es.js';
import { renderNotification, activateNotification } from './notification-bell.js';
import { renderPagerControls, MAX_PAGES } from './pager.js';

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** Etiqueta del grupo: Hoy, Ayer o la fecha larga. */
function dayLabel(iso) {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return null;

  const hoy = new Date();
  const mismoDia = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (mismoDia(fecha, hoy)) return notificationBellES.today;

  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (mismoDia(fecha, ayer)) return notificationBellES.yesterday;

  return fecha.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

class NotificationList {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    if (!this.container) throw new Error('company-theme: contenedor de notificaciones no encontrado');

    this.options = options;
    this.items = (options.items || []).slice();
    this.filter = 'all';

    this.pageSize = options.pageSize === 0 ? 0 : options.pageSize || 20;
    this.maxPages = options.maxPages || MAX_PAGES;
    this.page = 1;

    // Modo remoto, igual que en la vista de datos: con `url` el componente
    // consulta él mismo. Un historial de notificaciones crece sin parar, así que
    // traerlo entero es justo lo que no se puede hacer.
    this.url = options.url || null;
    this.total = options.total || 0;
    this.params = {
      page: 'page',
      limit: 'limit',
      filter: 'filter',
      ...(options.params || {}),
    };
    this.controller = null;

    this._build();

    if (this.url) this.reload();
    else this._render();
  }

  get unread() {
    return this.items.filter((item) => !item.read).length;
  }

  /** Las que pasan el filtro. En remoto filtra el servidor. */
  get filtered() {
    if (this.url || this.filter !== 'unread') return this.items;
    return this.items.filter((item) => !item.read);
  }

  get totalRows() {
    return this.url ? this.total : this.filtered.length;
  }

  get totalPages() {
    if (!this.pageSize) return 1;
    return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
  }

  /** Las que se ven: filtradas y de la página actual. */
  get visible() {
    const rows = this.filtered;
    // En remoto la página ya viene servida: repartirla otra vez la partiría.
    if (!this.pageSize || this.url) return rows;

    const start = (this.page - 1) * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  _build() {
    this.container.innerHTML = '';
    this.container.classList.add('company-notifications');

    const header = el('div', 'company-notifications-header');

    // Filtro. Dos botones y no un desplegable: son dos opciones, y esconderlas
    // detrás de un clic extra no ahorra nada.
    this.tabs = el('div', 'company-notifications-tabs');

    this.tabAll = el('button', 'company-notifications-tab is-active', notificationBellES.all);
    this.tabAll.type = 'button';
    this.tabAll.addEventListener('click', () => this.setFilter('all'));

    this.tabUnread = el('button', 'company-notifications-tab');
    this.tabUnread.type = 'button';
    this.tabUnread.addEventListener('click', () => this.setFilter('unread'));

    this.tabs.appendChild(this.tabAll);
    this.tabs.appendChild(this.tabUnread);
    header.appendChild(this.tabs);

    this.readAll = el('button', 'btn btn-outline-primary btn-sm', notificationBellES.readAll);
    this.readAll.type = 'button';
    this.readAll.addEventListener('click', () => this.markAllRead());
    header.appendChild(this.readAll);

    this.container.appendChild(header);

    this.list = el('div', 'company-notifications-list');
    this.container.appendChild(this.list);

    this.pager = el('nav', 'data-pager company-notifications-pager');
    this.pager.hidden = true;
    this.container.appendChild(this.pager);
  }

  /**
   * Pide la página al servidor.
   *
   * Responde `{ results, total }`, y el filtro viaja como parámetro: en remoto
   * quien decide qué está sin leer es el backend, porque aquí solo hay una
   * página y contar sobre ella daría un número falso.
   */
  reload() {
    if (!this.url) return this;

    // Se cancela lo anterior en vez de rechazar lo nuevo: al revés, el último
    // clic del usuario se pierde mientras llega lo que ya no mira.
    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    const url = new URL(this.url, window.location.href);
    url.searchParams.set(this.params.page, String(this.page));
    url.searchParams.set(this.params.limit, String(this.pageSize || 20));
    if (this.filter === 'unread') url.searchParams.set(this.params.filter, 'unread');

    this.container.classList.add('is-loading');

    fetch(url.toString(), {
      signal: this.controller ? this.controller.signal : undefined,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((json) => {
        this.container.classList.remove('is-loading');

        this.items = json.results || json.data || [];
        if (typeof json.total === 'number') this.total = json.total;
        if (typeof json.unread === 'number') this.remoteUnread = json.unread;

        this._render();

        this.container.dispatchEvent(
          new CustomEvent('loaded.company.notifications', {
            bubbles: true,
            detail: { page: this.page, total: this.total },
          })
        );
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.container.classList.remove('is-loading');
        this._showError();
      });

    return this;
  }

  /** Un fallo de red que deja la lista vacía se lee como «no hay nada». */
  _showError() {
    this.list.innerHTML = '';
    this.pager.hidden = true;

    const caja = el('div', 'error-block');
    caja.innerHTML = `
      <i class="fas fa-triangle-exclamation error-block-icon"></i>
      <p class="error-block-title">${dataViewES.loadError}</p>
      <p class="error-block-text">${dataViewES.loadErrorText}</p>
      <div class="error-block-actions">
        <button type="button" class="btn btn-outline-primary btn-sm" data-retry>
          <i class="fas fa-rotate-right me-2"></i>${dataViewES.retry}
        </button>
      </div>
    `;
    caja.querySelector('[data-retry]').addEventListener('click', () => this.reload());
    this.list.appendChild(caja);
  }

  _render() {
    // En remoto el contador lo dice el servidor: aquí solo hay una página, y
    // contar sobre ella daría «2 sin leer» habiendo doscientas.
    const pendientes = this.url ? this.remoteUnread || 0 : this.unread;

    // El contador va en la pestaña, no en un badge aparte: es la misma
    // información y repetirla obliga a mirar dos sitios.
    this.tabUnread.textContent = pendientes
      ? `${notificationBellES.unread} (${pendientes})`
      : notificationBellES.unread;

    this.tabAll.classList.toggle('is-active', this.filter === 'all');
    this.tabUnread.classList.toggle('is-active', this.filter === 'unread');
    this.readAll.hidden = pendientes === 0;

    this.list.innerHTML = '';

    const visibles = this.visible;

    if (!visibles.length) {
      // Dos vacíos distintos: no hay ninguna, o no queda ninguna sin leer.
      const texto =
        this.filter === 'unread' && this.items.length
          ? notificationBellES.allRead
          : this.options.emptyText || notificationBellES.empty;

      const caja = el('div', 'data-view-empty');
      caja.appendChild(el('i', 'fas fa-bell-slash data-view-empty-icon'));
      caja.appendChild(el('p', 'data-view-empty-title', texto));
      this.list.appendChild(caja);
      this.pager.hidden = true;
      return;
    }

    this._renderGroups(visibles);
    this._renderPager();
  }

  _renderPager() {
    const pages = this.totalPages;
    const total = this.totalRows;

    // El pie se muestra siempre que haya algo: el recuento es información por
    // sí mismo, aunque quepa todo en una página.
    this.pager.hidden = !this.pageSize;
    if (this.pager.hidden) return;

    this.pager.innerHTML = '';

    const resumen = el(
      'span',
      'data-pager-range',
      total === 1
        ? dataViewES.summaryOne
        : dataViewES.summary
            .replace('{pagina}', this.page)
            .replace('{paginas}', pages)
            .replace('{total}', total)
    );

    const izquierda = el('div', 'data-pager-summary');
    izquierda.appendChild(resumen);
    this.pager.appendChild(izquierda);

    const controles = renderPagerControls({
      page: this.page,
      pages,
      maxPages: this.maxPages,
      onPage: (n) => this.goToPage(n),
      previousLabel: dataViewES.previous,
      nextLabel: dataViewES.next,
    });

    if (controles) this.pager.appendChild(controles);
  }

  /**
   * @param {number} page
   */
  goToPage(page) {
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.page) return this;

    this.page = target;

    if (this.url) this.reload();
    else this._render();

    // Con la lista larga, cambiar de página deja al usuario mirando el medio de
    // la siguiente. Se sube al principio del listado, no de la ventana: la
    // pantalla puede tener más cosas encima.
    this.container.scrollIntoView({ block: 'start', behavior: 'smooth' });

    this.container.dispatchEvent(
      new CustomEvent('pageChanged.company.notifications', {
        bubbles: true,
        detail: { page: this.page, pages: this.totalPages },
      })
    );

    return this;
  }

  _renderGroups(items) {
    // Sin `date` no se agrupa: el theme no adivina la fecha del texto de `time`.
    const conFecha = items.filter((item) => item.date);

    if (conFecha.length !== items.length) {
      items.forEach((item) => this.list.appendChild(this._item(item)));
      return;
    }

    const grupos = new Map();

    items.forEach((item) => {
      const etiqueta = dayLabel(item.date) || '';
      if (!grupos.has(etiqueta)) grupos.set(etiqueta, []);
      grupos.get(etiqueta).push(item);
    });

    grupos.forEach((deEseDia, etiqueta) => {
      this.list.appendChild(el('p', 'company-notifications-day', etiqueta));
      deEseDia.forEach((item) => this.list.appendChild(this._item(item)));
    });
  }

  _item(item) {
    return renderNotification(
      item,
      (i, event, node) =>
        activateNotification({
          item: i,
          event,
          node,
          readUrl: this.options.readUrl,
          readMethod: this.options.readMethod,
          onDone: (marcada) => this.markRead(marcada),
          onError: (error) => {
            if (this.options.onReadError) this.options.onReadError(error, i);
            this.container.dispatchEvent(
              new CustomEvent('readError.company.notifications', {
                bubbles: true,
                detail: { error, item: i },
              })
            );
          },
        }),
      'company-bell'
    );
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  /**
   * @param {'all'|'unread'} filter
   */
  setFilter(filter) {
    const nuevo = filter === 'unread' ? 'unread' : 'all';
    if (nuevo === this.filter) return this;

    this.filter = nuevo;
    this.page = 1; // lo filtrado casi nunca tiene tantas páginas como el total

    if (this.url) this.reload();
    else this._render();

    return this;
  }

  setItems(items = []) {
    this.items = items.slice();
    this.page = 1;
    this._render();
    return this;
  }

  markRead(item) {
    if (!item || item.read) return this;

    item.read = true;
    this._render();

    if (this.options.onRead) this.options.onRead(item);
    this.container.dispatchEvent(
      new CustomEvent('read.company.notifications', {
        bubbles: true,
        detail: { item, unread: this.unread },
      })
    );

    return this;
  }

  markAllRead() {
    if (!this.unread) return this;

    this.items.forEach((item) => {
      item.read = true;
    });
    this._render();

    if (this.options.onReadAll) this.options.onReadAll();
    this.container.dispatchEvent(
      new CustomEvent('readAll.company.notifications', { bubbles: true, detail: { unread: 0 } })
    );

    return this;
  }

  dispose() {
    this.container.innerHTML = '';
    this.container.classList.remove('company-notifications');
    delete this.container.__companyNotifications;
  }
}

/**
 * @param {string|HTMLElement} container
 * @param {Object} options
 * @returns {NotificationList}
 */
export function notificationList(container, options) {
  const list = new NotificationList(container, options);
  list.container.__companyNotifications = list;
  return list;
}

export { NotificationList };
